<script setup>
import { computed, reactive } from "vue";
import { usePage } from "@inertiajs/vue3";
import ApplicationLogo from "@/Components/ApplicationLogo.vue";
import MarketingLayout from "@/Layouts/MarketingLayout.vue";
import Faq from "@/Components/Marketing/Faq.vue";
import TrustBadges from "@/Components/Marketing/TrustBadges.vue";
import { useChatwoot } from "@/Composables/useChatwoot.js";

const page = usePage();

const faqItems = [
    {
        q: "Hoe wordt er gefactureerd?",
        a: "We factureren op jaarbasis vooraf. U krijgt een duidelijke factuur per module op basis van uw gekozen pakket en aantal gebruikers of workflows.",
    },
    {
        q: "Kan ik later modules toevoegen of verwijderen?",
        a: "Ja, u kunt op elk moment uitbreiden. Bij het toevoegen van een module verrekenen we het verschil pro rata voor de resterende periode.",
    },
    {
        q: "Hoe telt besapro het aantal verkopers voor Assign?",
        a: "Assign rekent enkel de verkopers die leads ontvangen, met een minimum van 5. Het aantal pipelines, regels en toewijzingen is onbeperkt.",
    },
    {
        q: "Wat gebeurt er als mijn aantal gebruikers wijzigt?",
        a: "Bij groei verrekenen we extra gebruikers pro rata. Bij krimp passen we het aantal aan bij de volgende verlenging, u betaalt dus nooit te veel.",
    },
    {
        q: "Is er een proefperiode?",
        a: "U kunt een gratis demo aanvragen via info@besapro.eu en in overleg een proefperiode afspreken zodat u rustig kunt evalueren of besapro past.",
    },
    {
        q: "Zijn de prijzen inclusief btw?",
        a: "Alle vermelde prijzen zijn exclusief btw. We factureren conform de Belgische btw-regels en passen verlegging toe waar van toepassing.",
    },
    {
        q: "Welke ondersteuning krijg ik?",
        a: "Elke module bevat e-mailondersteuning en een uitgebreide kennisbank. Voor implementatie of maatwerk staan we klaar via info@besapro.eu of de chat in de applicatie.",
    },
];

const yearlyPrices = {
    timesheets: 120,
    assign: 60,
};

const assignMinimumUsers = 5;

const syncBaseYearlyPrice = 240;
const syncIncludedWorkflows = 2;
const syncAdditionalWorkflowYearlyPrice = 60;

const calculator = reactive({
    timesheetsUsers: 5,
    assignUsers: 5,
    workflows: 2,
    packages: {
        timesheets: true,
        sync: false,
        assign: false,
    },
});

const selectedPackages = computed(() => {
    return Object.entries(calculator.packages)
        .filter(([, selected]) => selected)
        .map(([packageKey]) => packageKey);
});

const billableAssignUsers = computed(() => {
    return Math.max(calculator.assignUsers || 0, assignMinimumUsers);
});

const syncYearlyPrice = computed(() => {
    const extraWorkflows = Math.max(calculator.workflows - syncIncludedWorkflows, 0);

    return syncBaseYearlyPrice + (extraWorkflows * syncAdditionalWorkflowYearlyPrice);
});

const packageUsers = (packageKey) => {
    if (packageKey === "timesheets") {
        return calculator.timesheetsUsers;
    }

    if (packageKey === "assign") {
        return billableAssignUsers.value;
    }

    return 0;
};

const moduleBreakdown = computed(() => {
    return selectedPackages.value.map((packageKey) => {
        if (packageKey === "sync") {
            return {
                key: packageKey,
                name: "Sync",
                detail: `${calculator.workflows} workflows`,
                amount: syncYearlyPrice.value,
            };
        }

        const yearlyPrice = yearlyPrices[packageKey] ?? 0;
        const users = packageUsers(packageKey);

        return {
            key: packageKey,
            name: packageKey === "timesheets" ? "Timesheets" : "Assign",
            detail: packageKey === "assign" ? `${users} verkopers` : `${users} gebruikers`,
            amount: yearlyPrice * users,
        };
    });
});

const total = computed(() => {
    return moduleBreakdown.value.reduce((sum, module) => sum + module.amount, 0);
});

const modulePriceLabel = (packageKey) => {
    if (packageKey === "sync") {
        return `${formatEuro(syncBaseYearlyPrice)} per jaar voor ${syncIncludedWorkflows} workflows`;
    }

    const yearlyPrice = yearlyPrices[packageKey] ?? 0;

    if (packageKey === "assign") {
        return `${formatEuro(yearlyPrice)} per verkoper (per jaar), vanaf ${assignMinimumUsers} verkopers`;
    }

    return `${formatEuro(yearlyPrice)} per gebruiker (per jaar)`;
};

const syncPricingDescription = computed(() => {
    return `${formatEuro(syncBaseYearlyPrice)} per jaar voor ${syncIncludedWorkflows} workflows, daarna ${formatEuro(syncAdditionalWorkflowYearlyPrice)} per extra workflow (per jaar).`;
});

const euro = new Intl.NumberFormat("nl-NL", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
});

const formatEuro = (value) => {
    return euro.format(value);
};

const { openChat } = useChatwoot();
</script>

<template>
  <MarketingLayout
    :menu="true"
    title="Prijzen"
  >
    <div class="bg-white py-24 sm:py-32">
      <div class="mx-auto max-w-7xl px-6 lg:px-8">
        <div class="mx-auto max-w-4xl text-center">
          <h2 class="text-base/7 font-semibold text-indigo-600">
            Prijzen
          </h2>
          <p class="mt-2 text-5xl font-semibold tracking-tight text-balance text-gray-900 sm:text-6xl">
            Prijzen
            die met u meegroeien
          </p>
        </div>
        <p class="mx-auto mt-6 max-w-2xl text-center text-lg font-medium text-pretty text-gray-600 sm:text-xl/8">
          Kies een betaalbaar pakket dat voorzien is van de beste functies voor het betrekken van uw publiek,
          het creëren van klantloyaliteit en het stimuleren van verkoop.
        </p>
        <div class="isolate mx-auto mt-10 grid max-w-md grid-cols-1 gap-8 lg:mx-0 lg:max-w-none lg:grid-cols-3">
          <div class="rounded-3xl p-8 ring-1 ring-gray-200 xl:p-10">
            <h3
              id="tier-freelancer"
              class="flex items-center gap-2 text-lg/8 font-semibold text-gray-900"
            >
              <ApplicationLogo logo="timesheets" />
              <span>Timesheets</span>
            </h3>
            <p class="mt-4 min-h-12 text-sm/6 text-gray-600">
              De essentiële functies om uw beste werk voor klanten te
              leveren.
            </p>
            <div class="mt-6 min-h-18">
              <p class="flex items-baseline gap-x-1">
                <span class="text-4xl font-semibold tracking-tight text-gray-900">€120</span>
                <span class="text-sm/6 font-semibold text-gray-600">/jaar per gebruiker</span>
              </p>
            </div>
            <a
              :href="page.props.timesheetsUrl"
              target="_blank"
              rel="noopener noreferrer"
              aria-describedby="tier-freelancer"
              class="mt-6 block rounded-md bg-indigo-600 px-3 py-2 text-center text-sm/6 font-semibold text-white shadow-2xs hover:bg-indigo-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
            >Begin nu</a>
            <button
              type="button"
              aria-describedby="tier-freelancer"
              class="mt-2 block w-full rounded-md px-3 py-1.5 text-center text-xs font-semibold text-indigo-600 hover:text-indigo-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
              @click="openChat"
            >
              Praat met sales
            </button>
            <ul
              role="list"
              class="mt-8 space-y-3 text-sm/6 text-gray-600 xl:mt-10"
            >
              <li class="flex gap-x-3">
                <svg
                  class="h-6 w-5 flex-none text-indigo-600"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden="true"
                  data-slot="icon"
                >
                  <path
                    fill-rule="evenodd"
                    d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z"
                    clip-rule="evenodd"
                  />
                </svg>
                Volledige toegang
              </li>
              <li class="flex gap-x-3">
                <svg
                  class="h-6 w-5 flex-none text-indigo-600"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden="true"
                  data-slot="icon"
                >
                  <path
                    fill-rule="evenodd"
                    d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z"
                    clip-rule="evenodd"
                  />
                </svg>
                Geen beperkingen
              </li>
              <li class="flex gap-x-3">
                <svg
                  class="h-6 w-5 flex-none text-indigo-600"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden="true"
                  data-slot="icon"
                >
                  <path
                    fill-rule="evenodd"
                    d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z"
                    clip-rule="evenodd"
                  />
                </svg>
                Integraties
              </li>
            </ul>
          </div>
          <div class="rounded-3xl p-8 ring-1 ring-gray-200 xl:p-10">
            <h3
              id="tier-startup"
              class="flex items-center gap-2 text-lg/8 font-semibold text-gray-900"
            >
              <ApplicationLogo logo="sync" />
              <span>Sync</span>
            </h3>
            <p class="mt-4 min-h-12 text-sm/6 text-gray-600">
              Koppel uw systemen met flexibele integraties die meegroeien met uw organisatie.
            </p>
            <div class="mt-6 min-h-18">
              <p class="flex items-baseline gap-x-1">
                <span class="text-4xl font-semibold tracking-tight text-gray-900">€240</span>
                <span class="text-sm/6 font-semibold text-gray-600">/jaar voor 2 workflows</span>
              </p>
              <p class="mt-2 text-sm/6 text-gray-600">
                + €60 per extra workflow (per jaar)
              </p>
            </div>
            <a
              :href="page.props.syncUrl"
              target="_blank"
              rel="noopener noreferrer"
              aria-describedby="tier-startup"
              class="mt-6 block rounded-md bg-indigo-600 px-3 py-2 text-center text-sm/6 font-semibold text-white shadow-2xs hover:bg-indigo-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
            >Begin nu</a>
            <button
              type="button"
              aria-describedby="tier-startup"
              class="mt-2 block w-full rounded-md px-3 py-1.5 text-center text-xs font-semibold text-indigo-600 hover:text-indigo-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
              @click="openChat"
            >
              Praat met sales
            </button>
            <ul
              role="list"
              class="mt-8 space-y-3 text-sm/6 text-gray-600 xl:mt-10"
            >
              <li class="flex gap-x-3">
                <svg
                  class="h-6 w-5 flex-none text-indigo-600"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden="true"
                  data-slot="icon"
                >
                  <path
                    fill-rule="evenodd"
                    d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z"
                    clip-rule="evenodd"
                  />
                </svg>
                Workflows, triggers en uitgaande verbindingen
              </li>
            </ul>
          </div>
          <div class="rounded-3xl p-8 ring-1 ring-gray-200 xl:p-10">
            <h3
              id="tier-assign"
              class="flex items-center gap-2 text-lg/8 font-semibold text-gray-900"
            >
              <ApplicationLogo logo="assign" />
              <span>Assign</span>
            </h3>
            <p class="mt-4 min-h-12 text-sm/6 text-gray-600">
              Plan en wijs werk eenvoudig toe aan uw team.
            </p>
            <div class="mt-6 min-h-18">
              <p class="flex items-baseline gap-x-1">
                <span class="text-4xl font-semibold tracking-tight text-gray-900">€60</span>
                <span class="text-sm/6 font-semibold text-gray-600">/jaar per verkoper</span>
              </p>
              <p class="mt-1 text-sm/6 text-gray-600">
                Vanaf 5 verkopers, onbeperkt pipelines en regels
              </p>
            </div>
            <a
              :href="page.props.assignUrl"
              target="_blank"
              rel="noopener noreferrer"
              aria-describedby="tier-assign"
              class="mt-6 block rounded-md bg-indigo-600 px-3 py-2 text-center text-sm/6 font-semibold text-white shadow-2xs hover:bg-indigo-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
            >Begin nu</a>
            <button
              type="button"
              aria-describedby="tier-assign"
              class="mt-2 block w-full rounded-md px-3 py-1.5 text-center text-xs font-semibold text-indigo-600 hover:text-indigo-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
              @click="openChat"
            >
              Praat met sales
            </button>
            <ul
              role="list"
              class="mt-8 space-y-3 text-sm/6 text-gray-600 xl:mt-10"
            >
              <li class="flex gap-x-3">
                <svg
                  class="h-6 w-5 flex-none text-indigo-600"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden="true"
                  data-slot="icon"
                >
                  <path
                    fill-rule="evenodd"
                    d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z"
                    clip-rule="evenodd"
                  />
                </svg>
                Slimme planning
              </li>
              <li class="flex gap-x-3">
                <svg
                  class="h-6 w-5 flex-none text-indigo-600"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden="true"
                  data-slot="icon"
                >
                  <path
                    fill-rule="evenodd"
                    d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z"
                    clip-rule="evenodd"
                  />
                </svg>
                Taken toewijzen per gebruiker
              </li>
              <li class="flex gap-x-3">
                <svg
                  class="h-6 w-5 flex-none text-indigo-600"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden="true"
                  data-slot="icon"
                >
                  <path
                    fill-rule="evenodd"
                    d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z"
                    clip-rule="evenodd"
                  />
                </svg>
                Duidelijk teamoverzicht
              </li>
            </ul>
          </div>
        </div>
        <div class="mx-auto mt-20 max-w-5xl rounded-3xl bg-gray-50 p-8 ring-1 ring-gray-200 lg:p-10">
          <div class="max-w-2xl">
            <h2 class="text-3xl font-semibold tracking-tight text-gray-900">
              Prijs calculator
            </h2>
            <p class="mt-4 text-base text-gray-600">
              Bereken uw jaarprijs op basis van het aantal gebruikers en gekozen modules.
            </p>
          </div>

          <div class="mt-10 grid gap-8 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,0.8fr)]">
            <div class="space-y-8">
              <fieldset>
                <legend class="text-sm font-semibold text-gray-900">
                  Modules
                </legend>
                <div class="mt-3 grid gap-3">
                  <div class="space-y-3">
                    <label
                      class="flex cursor-pointer items-start gap-4 rounded-2xl bg-white px-4 py-4 ring-1 ring-gray-200 transition hover:ring-indigo-300"
                      :class="calculator.packages.timesheets ? 'ring-2 ring-indigo-600' : ''"
                    >
                      <input
                        v-model="calculator.packages.timesheets"
                        type="checkbox"
                        class="mt-1 h-4 w-4 rounded-sm border-gray-300 text-indigo-600 focus:ring-indigo-600"
                      >
                      <div class="min-w-0">
                        <div class="flex items-center gap-2 text-sm font-semibold text-gray-900">
                          <ApplicationLogo logo="timesheets" />
                          <span>Timesheets</span>
                        </div>
                        <p class="mt-1 text-sm text-gray-500">{{ modulePriceLabel('timesheets') }}</p>
                      </div>
                    </label>
                    <div
                      v-if="calculator.packages.timesheets"
                      class="flex items-center gap-4 pl-8 sm:pl-12"
                    >
                      <label
                        for="calculator-timesheets-users"
                        class="text-sm font-medium text-gray-900"
                      >Aantal gebruikers</label>
                      <div class="flex w-full max-w-[220px] items-center rounded-xl bg-white ring-1 ring-gray-200 focus-within:ring-2 focus-within:ring-indigo-600">
                        <input
                          id="calculator-timesheets-users"
                          v-model.number="calculator.timesheetsUsers"
                          type="number"
                          min="1"
                          class="w-full rounded-xl border-0 bg-transparent px-4 py-2.5 text-base text-gray-900 outline-hidden ring-0 placeholder:text-gray-400"
                        >
                      </div>
                    </div>
                  </div>
                  <div class="space-y-3">
                    <label
                      class="flex cursor-pointer items-start gap-4 rounded-2xl bg-white px-4 py-4 ring-1 ring-gray-200 transition hover:ring-indigo-300"
                      :class="calculator.packages.sync ? 'ring-2 ring-indigo-600' : ''"
                    >
                      <input
                        v-model="calculator.packages.sync"
                        type="checkbox"
                        class="mt-1 h-4 w-4 rounded-sm border-gray-300 text-indigo-600 focus:ring-indigo-600"
                      >
                      <div class="min-w-0">
                        <div class="flex items-center gap-2 text-sm font-semibold text-gray-900">
                          <ApplicationLogo logo="sync" />
                          <span>Sync</span>
                        </div>
                        <p class="mt-1 text-sm text-gray-500">{{ syncPricingDescription }}</p>
                      </div>
                    </label>
                    <div
                      v-if="calculator.packages.sync"
                      class="pl-8 sm:pl-12"
                    >
                      <div class="flex items-center gap-4">
                        <label
                          for="calculator-workflows"
                          class="text-sm font-medium text-gray-900"
                        >Aantal workflows</label>
                        <div class="flex w-full max-w-[220px] items-center rounded-xl bg-white ring-1 ring-gray-200 focus-within:ring-2 focus-within:ring-indigo-600">
                          <input
                            id="calculator-workflows"
                            v-model.number="calculator.workflows"
                            type="number"
                            min="2"
                            class="w-full rounded-xl border-0 bg-transparent px-4 py-2.5 text-base text-gray-900 outline-hidden ring-0 placeholder:text-gray-400"
                          >
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="space-y-3">
                    <label
                      class="flex cursor-pointer items-start gap-4 rounded-2xl bg-white px-4 py-4 ring-1 ring-gray-200 transition hover:ring-indigo-300"
                      :class="calculator.packages.assign ? 'ring-2 ring-indigo-600' : ''"
                    >
                      <input
                        v-model="calculator.packages.assign"
                        type="checkbox"
                        class="mt-1 h-4 w-4 rounded-sm border-gray-300 text-indigo-600 focus:ring-indigo-600"
                      >
                      <div class="min-w-0">
                        <div class="flex items-center gap-2 text-sm font-semibold text-gray-900">
                          <ApplicationLogo logo="assign" />
                          <span>Assign</span>
                        </div>
                        <p class="mt-1 text-sm text-gray-500">{{ modulePriceLabel('assign') }}</p>
                      </div>
                    </label>
                    <div
                      v-if="calculator.packages.assign"
                      class="flex items-center gap-4 pl-8 sm:pl-12"
                    >
                      <label
                        for="calculator-assign-users"
                        class="text-sm font-medium text-gray-900"
                      >Aantal verkopers</label>
                      <div class="flex w-full max-w-[220px] items-center rounded-xl bg-white ring-1 ring-gray-200 focus-within:ring-2 focus-within:ring-indigo-600">
                        <input
                          id="calculator-assign-users"
                          v-model.number="calculator.assignUsers"
                          type="number"
                          :min="assignMinimumUsers"
                          class="w-full rounded-xl border-0 bg-transparent px-4 py-2.5 text-base text-gray-900 outline-hidden ring-0 placeholder:text-gray-400"
                        >
                      </div>
                    </div>
                  </div>
                </div>
              </fieldset>
            </div>

            <div class="rounded-3xl bg-gray-900 p-8 text-white">
              <p class="text-sm font-semibold text-indigo-300">
                Uw prijs
              </p>
              <p class="mt-4 text-5xl font-semibold tracking-tight">
                {{ formatEuro(total) }}
              </p>
              <p class="mt-2 text-sm text-gray-300">
                /jaar, jaarlijks gefactureerd
              </p>

              <dl class="mt-8 space-y-4 text-sm text-gray-300">
                <div
                  v-for="module in moduleBreakdown"
                  :key="module.key"
                  class="flex items-center justify-between gap-4"
                >
                  <dt>{{ module.name }} <span class="text-gray-400">({{ module.detail }})</span></dt>
                  <dd>{{ formatEuro(module.amount) }}</dd>
                </div>
                <div class="border-t border-white/10 pt-4">
                  <div class="flex items-center justify-between gap-4 text-base font-semibold text-white">
                    <dt>Totaal</dt>
                    <dd>{{ formatEuro(total) }}</dd>
                  </div>
                </div>
              </dl>

              <p
                v-if="selectedPackages.length === 0"
                class="mt-6 rounded-2xl bg-white/5 px-4 py-3 text-sm text-gray-300"
              >
                Selecteer minstens een module om een prijs te berekenen.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <Faq
      :items="faqItems"
      intro="Antwoorden op de vragen die we het vaakst krijgen rond prijs en facturatie."
    />

    <TrustBadges />
  </MarketingLayout>
</template>

<style scoped>

</style>
